import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icons/icon-reusable';
import { calenderIcon } from '../icons/icon';
import { registerApiFieldTarget } from '../../../service/api-field-errors.service';

interface CalendarDay {
  iso: string;
  day: number;
  inCurrentMonth: boolean;
}

@Component({
  selector: 'app-date-picker-reusable',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IconComponent],
  templateUrl: './date-picker-reusable.html',
  styles: [`
    /* A range's two dates are each exactly as wide as their text, so they sit close together
       with the dash centred between them. Browsers without field-sizing get a near fit. */
    .range-date {
      width: 10.5ch;
      font-variant-numeric: tabular-nums;
    }
    @supports (field-sizing: content) {
      .range-date {
        field-sizing: content;
        width: auto;
      }
    }
  `],
})
export class DatePickerReusableComponent implements OnInit, OnDestroy {
  readonly calenderIcon = calenderIcon;
  private static readonly overlayGap = 8;
  private static readonly overlayMinHeight = 320;
  /** Floor for the year dropdown when no `minDate` narrows it (old enough for any DOB). */
  private static readonly defaultMinYearOption = 1900;
  /** Ceiling for the year dropdown when no `maxDate` (or `disableFutureDates`) narrows it. */
  private static readonly defaultMaxYearOption = 2100;
  /** Space kept between the calendar and the screen's edges. */
  private static readonly viewportMargin = 16;
  /** One month's width; a range shows two side by side when the screen has room for both. */
  private static readonly monthWidth = 320;
  private static readonly rangeSideBySideHeight = 400;
  private static readonly rangeStackedHeight = 720;
  /** Sentinel "no upper bound" year, used for range maths only — never listed in the dropdown. */
  private static readonly maxYearOption = 9999;
  /** Cap on memoised month grids so wheel-scrolling years cannot grow the cache unbounded. */
  private static readonly calendarCacheLimit = 24;
 
  private valueToIso(value: unknown): string {
    if (value == null) return '';
    return this.normalizeToIsoDate(value) ?? '';
  }
 
  @Input() control?: FormControl | AbstractControl | null;
  @Input() startControl?: FormControl | AbstractControl | null;
  @Input() endControl?: FormControl | AbstractControl | null;
  @Input() mode: 'single' | 'range' = 'single';
  @Input() size: 'sm' | 'md' | 'lg' = 'lg';
  @Input() iconSize?: number;
  @Input() placeholder = 'dd-mm-yyyy';
  @Input() maxDate?: string;
  @Input() disableFutureDates = false;
  @Input() minDate?: string;
 
  @Output() valueChange = new EventEmitter<any>();

  get computedIconSize(): number {
    return this.iconSize ?? 16;
  }
 
  protected isOpen = false;
  protected openAbove = false;
  /** A range's two months sit side by side when the screen is wide enough, else stacked. */
  protected rangeSideBySide = false;
  protected overlayStyle: Record<string, string> = {};
  protected leftMonth = this.startOfMonth(this.getDefaultCalendarDate());
  protected rightMonth = this.addMonths(this.leftMonth, 1);
  protected weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  protected monthOptions = [
    { label: 'January', value: 0 },
    { label: 'February', value: 1 },
    { label: 'March', value: 2 },
    { label: 'April', value: 3 },
    { label: 'May', value: 4 },
    { label: 'June', value: 5 },
    { label: 'July', value: 6 },
    { label: 'August', value: 7 },
    { label: 'September', value: 8 },
    { label: 'October', value: 9 },
    { label: 'November', value: 10 },
    { label: 'December', value: 11 },
  ];
  protected yearOptions: number[] = [];
  protected currentYear = new Date().getFullYear();
 
  protected singleFocused = false;
  protected startFocused = false;
  protected endFocused = false;
  protected rawSingle = '';
  protected rawStart = '';
  protected rawEnd = '';
  protected pendingIso = '';
  protected pendingStartIso = '';
  protected pendingEndIso = '';
  private blurTimer: ReturnType<typeof setTimeout> | undefined;
  /**
   * Where the caret was when the box was last masked, counted in digits. Held
   * across the change detection that re-writes the input's value, which is what
   * would otherwise leave the caret at the end of the date.
   */
  private caretDigitIndex: number | null = null;
  private maxSelectableIso: string | null = null;
  private readonly calendarDaysCache = new Map<number, CalendarDay[]>();
 
  @ViewChild('triggerButton') private triggerButton?: ElementRef<HTMLElement>;

  /**
   * True when any bound control is invalid and has been interacted with.
   * Exposed as an `.ng-invalid` class on the host wrapper so the shared
   * scroll-to-first-error logic can locate this control in the DOM (the
   * native inputs here are not bound via [formControl], so Angular never
   * stamps `.ng-invalid` on them automatically).
   */
  protected get isInvalid(): boolean {
    return [this.control, this.startControl, this.endControl].some(
      (ctrl): ctrl is AbstractControl =>
        !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty),
    );
  }

  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  /** Lets a rejected save's field error find, mark and scroll to this field. */
  private readonly apiFieldTarget = registerApiFieldTarget(() => [this.control, this.startControl, this.endControl]);
 
  private readonly scrollListener = (): void => {
    if (this.isOpen) {
      this.updateOverlayPosition();
    }
  };
 
  private readonly outsideClickListener = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Node) || !this.hostElement.nativeElement.contains(target)) {
      this.closeOverlay();
      this.cdr.detectChanges();
    }
  };
 
  /**
   * The dropdown is bounded by the control's own limits, not by the sentinel
   * 1950-9999 span. That span produced 8,050 `<option>` nodes per select (16,100
   * in range mode) — every one of them a live binding that Angular re-checked on
   * each change-detection pass — which is what made the calendar slow to open.
   */
  private buildYearOptions(): number[] {
    const minYear =
      this.parseIsoDate(this.minDate)?.getFullYear() ??
      DatePickerReusableComponent.defaultMinYearOption;
    // When the caller bounds the control explicitly, honour that bound exactly;
    // otherwise fall back to a sane window around today.
    const explicitlyBounded = !!this.maxDate || this.disableFutureDates;
    const maxYear = explicitlyBounded
      ? this.getMaxSelectableDate().getFullYear()
      : DatePickerReusableComponent.defaultMaxYearOption;

    if (maxYear < minYear) return [minYear];

    const years: number[] = [];
    for (let year = minYear; year <= maxYear; year++) {
      years.push(year);
    }
    return years;
  }

  protected onYearWheel(which: 'left' | 'right', event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY === 0) return;

    const direction = event.deltaY < 0 ? -1 : 1;
    const activeMonth = which === 'left' ? this.leftMonth : this.rightMonth;
    const nextYear = Math.min(
      this.yearOptions[this.yearOptions.length - 1] ?? DatePickerReusableComponent.maxYearOption,
      Math.max(
        this.yearOptions[0] ?? DatePickerReusableComponent.defaultMinYearOption,
        activeMonth.getFullYear() + direction,
      ),
    );

    if (nextYear === activeMonth.getFullYear()) return;

    if (which === 'left') {
      this.leftMonth = new Date(nextYear, this.leftMonth.getMonth(), 1);
    } else {
      this.rightMonth = new Date(nextYear, this.rightMonth.getMonth(), 1);
    }
  }
 
private getOpeningMonth(): Date {

  if (this.minDate) {
    const min = this.parseIsoDate(this.minDate);
    if (min) return min;
  }

  if (this.maxDate) {
    const max = this.parseIsoDate(this.maxDate);
    if (max) return max;
  }

  return this.getDefaultCalendarDate();
}
 
  ngOnInit(): void {
    this.yearOptions = this.buildYearOptions();
    if (this.mode === 'single' && this.control) {
      const parsed = this.parseIsoDate(this.control.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      if (this.control.value) {
        this.rawSingle = this.formatDisplay(this.control.value);
      }
    }
    if (this.mode === 'range' && this.startControl) {
      const parsed = this.parseIsoDate(this.startControl.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.rightMonth = this.addMonths(this.leftMonth, 1);
      if (this.startControl.value) {
        this.rawStart = this.formatDisplay(this.startControl.value);
      }
      if (this.endControl?.value) {
        this.rawEnd = this.formatDisplay(this.endControl.value);
      }
    }
  }
 
  ngOnDestroy(): void {
    clearTimeout(this.blurTimer);
    this.closeOverlay();
  }
 
  private openCalendar(): void {
    // `disableFutureDates` is relative to "now", so drop the memo on each open
    // rather than letting a long-lived session keep yesterday's bound.
    this.maxSelectableIso = null;
    this.overlayStyle = {
      position: 'fixed',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '99999',
    };
    this.isOpen = true;
    this.cdr.detectChanges();
    this.updateOverlayPosition();
    this.bindGlobalListeners();
  }
 
  protected toggle(): void {
    if (this.control?.disabled || this.startControl?.disabled) return;
    if (this.isOpen) {
      this.closeOverlay();
      return;
    }
    if (this.mode === 'single') {
      const parsed = this.parseIsoDate(this.control?.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
    } else {
      const parsed = this.parseIsoDate(this.startControl?.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.rightMonth = this.addMonths(this.leftMonth, 1);
    }
    this.openCalendar();
  }
 
  protected onSingleFocus(): void {
    if (this.control?.disabled) return;
    clearTimeout(this.blurTimer);
    this.singleFocused = true;
    this.rawSingle = this.control?.value ? this.formatDisplay(this.control.value) : '';
    if (!this.isOpen) {
      const parsed = this.parseIsoDate(this.control?.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.openCalendar();
    }
  }
 
  protected onSingleInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskTypedInput(input, this.rawSingle);
    this.rawSingle = masked;
    const iso = this.parseTypedDate(masked);
    this.pendingIso = iso ?? '';
    if (iso) {
      this.leftMonth = this.startOfMonth(this.parseIsoDate(iso)!);
    }
    this.cdr.detectChanges();
    this.restoreCaret(input);
  }
 
  protected onSingleBlur(value: string): void {
    this.blurTimer = setTimeout(() => {
      this.singleFocused = false;
      this.pendingIso = '';
      const iso = this.parseTypedDate(value);
      if (iso) {
        this.control?.setValue(iso);
        this.control?.markAsTouched();
        this.valueChange.emit(iso);
      } else if (!value.trim()) {
        this.control?.setValue('');
        this.valueChange.emit('');
      }
      this.cdr.detectChanges();
    }, 200);
  }
 
  protected onStartFocus(): void {
    if (this.startControl?.disabled) return;
    clearTimeout(this.blurTimer);
    this.startFocused = true;
    this.rawStart = this.startControl?.value ? this.formatDisplay(this.startControl.value) : '';
    if (!this.isOpen) {
      const parsed = this.parseIsoDate(this.startControl?.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.rightMonth = this.addMonths(this.leftMonth, 1);
      this.openCalendar();
    }
  }
 
  protected onStartInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskTypedInput(input, this.rawStart);
    this.rawStart = masked;
    const iso = this.parseTypedDate(masked);
    this.pendingStartIso = iso ?? '';
    if (iso) {
      this.leftMonth = this.startOfMonth(this.parseIsoDate(iso)!);
    }
    this.cdr.detectChanges();
    this.restoreCaret(input);
  }
 
  protected onStartBlur(value: string): void {
    this.blurTimer = setTimeout(() => {
      this.startFocused = false;
      this.pendingStartIso = '';
      const iso = this.parseTypedDate(value);
      if (iso) {
        this.startControl?.setValue(iso);
        this.startControl?.markAsTouched();
        const endIso = this.normalizeToIsoDate(this.endControl?.value) ?? '';
        if (endIso && iso > endIso) {
          this.endControl?.setValue('');
          this.rawEnd = '';
        }
        this.valueChange.emit({ start: iso, end: this.endControl?.value || '' });
      } else if (!value.trim()) {
        this.startControl?.setValue('');
        this.endControl?.setValue('');
        this.rawEnd = '';
        this.valueChange.emit({ start: '', end: '' });
      }
      this.cdr.detectChanges();
    }, 200);
  }
 
  protected onEndFocus(): void {
    if (this.startControl?.disabled) return;
    clearTimeout(this.blurTimer);
    this.endFocused = true;
    this.rawEnd = this.endControl?.value ? this.formatDisplay(this.endControl.value) : '';
    if (!this.isOpen) {
      const ref = this.endControl?.value ?? this.startControl?.value;
      const parsed = this.parseIsoDate(ref);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.rightMonth = this.addMonths(this.leftMonth, 1);
      this.openCalendar();
    }
  }
 
  protected onEndInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskTypedInput(input, this.rawEnd);
    this.rawEnd = masked;
    const iso = this.parseTypedDate(masked);
    this.pendingEndIso = iso ?? '';
    if (iso) {
      const endDate = this.parseIsoDate(iso)!;
      this.rightMonth = this.startOfMonth(endDate);
      if (this.leftMonth >= this.rightMonth) {
        this.leftMonth = this.addMonths(this.rightMonth, -1);
      }
    }
    this.cdr.detectChanges();
    this.restoreCaret(input);
  }
 
  protected onEndBlur(value: string): void {
    this.blurTimer = setTimeout(() => {
      this.endFocused = false;
      this.pendingEndIso = '';
      const iso = this.parseTypedDate(value);
      const startIso = this.normalizeToIsoDate(this.startControl?.value) ?? '';
      if (iso && (!startIso || iso >= startIso)) {
        this.endControl?.setValue(iso);
        this.endControl?.markAsTouched();
        this.valueChange.emit({ start: startIso, end: iso });
      } else if (!value.trim()) {
        this.endControl?.setValue('');
        this.valueChange.emit({ start: startIso, end: '' });
      } else {
        this.rawEnd = this.endControl?.value ? this.formatDisplay(this.endControl.value) : '';
      }
      this.cdr.detectChanges();
    }, 200);
  }
 
  /**
   * Deleting across one of the separators. Left alone, the key takes the `-`
   * itself — which the mask puts straight back, so the field looks frozen, and
   * the parts either side run together into something the mask then re-cuts in
   * the wrong places. The digit the key is pointing at is taken instead and the
   * separator is left where it is.
   */
  protected onDateKeydown(event: KeyboardEvent): void {
    const isBackspace = event.key === 'Backspace';
    if (!isBackspace && event.key !== 'Delete') {
      this.overtypeDigit(event);
      return;
    }

    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    // Only a plain caret: a selection is the user saying exactly what to delete.
    if (start !== (input.selectionEnd ?? 0)) return;

    const value = input.value;
    let next: string;
    let caret: number;

    if (isBackspace) {
      if (start < 2 || value[start - 1] !== '-') return;
      // Drop the digit in front of the separator, keeping the separator.
      next = value.slice(0, start - 2) + value.slice(start - 1);
      caret = start - 2;
    } else {
      if (value[start] !== '-') return;
      // Drop the digit after the separator, keeping the separator.
      next = value.slice(0, start + 1) + value.slice(start + 2);
      caret = start;
    }

    event.preventDefault();
    input.value = next;
    input.setSelectionRange(caret, caret);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * A digit typed onto a digit replaces it rather than being pushed in beside
   * it. The parts are fixed width, so inserting would only shunt a digit off the
   * end of the part — typing over the `0` of a `09` day would leave the day as
   * the `0` and the new digit, not as the digit and the `9`.
   */
  private overtypeDigit(event: KeyboardEvent): void {
    if (event.key.length !== 1 || event.key < '0' || event.key > '9') return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    if (start !== (input.selectionEnd ?? 0)) return;

    // Nothing under the caret, or a separator: the ordinary insert applies.
    const target = input.value[start];
    if (!target || target === '-') return;

    event.preventDefault();
    input.value = input.value.slice(0, start) + event.key + input.value.slice(start + 1);
    input.setSelectionRange(start + 1, start + 1);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Masks what is in the box and works out where the caret belongs afterwards.
   *
   * The caret is remembered as a number of digits rather than a character index,
   * because the mask can add or drop separators around it. Where it is actually
   * put is left to `restoreCaret`: the input's value is bound, so Angular writes
   * it again during the change detection that follows — and writing an input's
   * value always drops the caret at the end.
   */
  private maskTypedInput(input: HTMLInputElement, previous: string): string {
    const typed = input.value;
    const caret = input.selectionStart ?? typed.length;
    let digitsBeforeCaret = this.countDigits(typed.slice(0, caret));
    let masked: string;

    if (caret >= typed.length) {
      // Typing at the end is the ordinary case and keeps the auto-advancing
      // mask, which inserts the separators and corrects an impossible day or
      // month as it goes.
      masked = this.maskWhileTyping(typed);
      // That mask can pad what was typed — a lone `9` becomes the 9th — so the
      // caret follows the text to the end rather than the digit it was behind.
      digitsBeforeCaret = this.countDigits(masked);
    } else {
      // An edit inside the date cannot use that mask: it re-flows the digits, so
      // deleting one digit of the month pulls the year's first digit into it and
      // leaves the year a digit short.
      const edited = this.maskInPlace(typed);

      if (this.isPartialDate(edited)) {
        masked = edited;
      } else {
        // The edit would leave an impossible day or month in the box. It is not
        // taken at all — the keystroke simply does not appear, rather than being
        // rewritten into some other date the user did not ask for.
        masked = previous;
        if (this.countDigits(typed) > this.countDigits(previous)) {
          digitsBeforeCaret--;
        }
      }
    }

    input.value = masked;
    this.caretDigitIndex = digitsBeforeCaret;
    return masked;
  }

  /**
   * Whether the box could still become a date. Judged as it stands, half-typed:
   * a lone `3` is a fine start for a day, `32` never is. The year is left to
   * `parseTypedDate`, which is what decides whether the whole thing is a date.
   */
  private isPartialDate(value: string): boolean {
    const [day = '', month = ''] = value.split('-');

    return this.isPartialPart(day, 31) && this.isPartialPart(month, 12);
  }

  private isPartialPart(part: string, max: number): boolean {
    // One digit is always a possible start — `0` leads to 01-09, `3` to 30/31.
    if (part.length < 2) return true;

    const parsed = Number(part);
    return parsed >= 1 && parsed <= max;
  }

  /** Puts the caret back after the digit it was sitting behind. */
  private restoreCaret(input: HTMLInputElement): void {
    if (this.caretDigitIndex === null) return;

    const position = this.caretForDigit(input.value, this.caretDigitIndex);
    this.caretDigitIndex = null;
    input.setSelectionRange(position, position);
  }

  /** Digits typed so far, flowed into `dd-mm-yyyy` as they arrive. */
  private maskWhileTyping(value: string): string {
    const d = this.constrainDateDigits(value.replace(/\D/g, ''));
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0, 2)}-${d.slice(2)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
  }

  /**
   * Each part masked where it stands: non-digits dropped and the part capped at
   * its own length, with nothing moving across a separator. Whether a part is
   * a sensible day or month is not judged here — the value is half-edited, and
   * `parseTypedDate` is what decides whether it is a date yet.
   */
  private maskInPlace(value: string): string {
    const parts = value.split('-');
    const lengths = [2, 2, 4];

    return parts
      .slice(0, 3)
      .map((part, index) => part.replace(/\D/g, '').slice(0, lengths[index]))
      .join('-');
  }

  private countDigits(text: string): number {
    let count = 0;
    for (const character of text) {
      if (character >= '0' && character <= '9') count++;
    }
    return count;
  }

  /** Index just past the nth digit of `text`, or its end when there are fewer. */
  private caretForDigit(text: string, digitCount: number): number {
    if (digitCount <= 0) return 0;

    let seen = 0;
    for (let index = 0; index < text.length; index++) {
      const character = text[index];
      if (character >= '0' && character <= '9') {
        seen++;
        if (seen === digitCount) return index + 1;
      }
    }

    return text.length;
  }
 
  private constrainDateDigits(digits: string): string {
    let result = '';
    let pos = 0;
 
    // Day first digit
    if (pos >= digits.length) return result;
    const d1 = Number(digits[pos]);
    if (d1 > 3) {
      result += '0' + d1; pos++;  // "7" → "07"
    } else {
      result += digits[pos++];
      if (pos < digits.length) {
        const d2 = Number(digits[pos]);
        const day = d1 * 10 + d2;
        if (day === 0) { result += '1'; pos++; }        // 00 → 01
        else if (day <= 31) { result += digits[pos++]; } // valid
        else { pos++; }                                   // >31: skip
      }
    }
 
    if (result.length < 2) return result;
 
    // Month first digit
    if (pos >= digits.length) return result;
    const m1 = Number(digits[pos]);
    if (m1 > 1) {
      result += '0' + m1; pos++;  // "9" → "09"
    } else {
      result += digits[pos++];
      if (pos < digits.length) {
        const m2 = Number(digits[pos]);
        const month = m1 * 10 + m2;
        if (month === 0) { result += '1'; pos++; }        // 00 → 01
        else if (month <= 12) { result += digits[pos++]; } // valid
        else { pos++; }                                     // >12: skip
      }
    }
 
    if (result.length < 4) return result;
 
 // Year: any 4 digits
    while (pos < digits.length && result.length < 8) {
      result += digits[pos++];
    }
 
    return result;
  }
 
  private parseTypedDate(text: string): string | null {
    const trimmed = text.trim();
    if (!trimmed) return null;
 
    const dmyMatch = trimmed.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})$/);
    if (dmyMatch) {
      const [, day, month, year] = dmyMatch;
      const date = this.buildValidatedDate(Number(year), Number(month), Number(day));
      if (date) {
        const iso = this.toIsoDate(date);
        return this.isDateDisabled(iso) ? null : iso;
      }
    }
 
    const isoMatch = trimmed.match(/^(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      const date = this.buildValidatedDate(Number(year), Number(month), Number(day));
      if (date) {
        const iso = this.toIsoDate(date);
        return this.isDateDisabled(iso) ? null : iso;
      }
    }
 
    return null;
  }
 
  private getDefaultCalendarDate(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }
 
  protected get todayIso(): string {
    return this.toIsoDate(new Date());
  }
 
  protected clearRange(event?: Event): void {
    event?.stopPropagation();
    if (this.mode === 'range' && this.startControl && this.endControl) {
      this.startControl.setValue('');
      this.endControl.setValue('');
      this.rawStart = '';
      this.rawEnd = '';
      this.pendingStartIso = '';
      this.pendingEndIso = '';
      this.valueChange.emit({ start: '', end: '' });
    }
  }
 
protected isDateDisabled(iso: string): boolean {

  if (this.minDate && iso < this.minDate) {
    return true;
  }

  if (iso > this.getMaxSelectableIsoDate()) {
    return true;
  }

  return false;
}
 
  protected isNextMonthDisabled(month: Date): boolean {
    const next = this.addMonths(month, 1);
    const nextIso = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`;
    return nextIso > this.getMaxSelectableIsoDate();
  }
 
  protected selectDate(iso: string): void {
    if (this.isDateDisabled(iso)) return;
 
    if (this.mode === 'single' && this.control) {
      clearTimeout(this.blurTimer);
      this.singleFocused = false;
      this.pendingIso = '';
      this.control.setValue(iso);
      this.control.markAsTouched();
      this.rawSingle = this.formatDisplay(iso);
      this.valueChange.emit(iso);
      this.closeOverlay();
      return;
    }
 
    if (this.mode === 'range' && this.startControl && this.endControl) {
      const start = String(this.startControl.value || '');
      const end = String(this.endControl.value || '');
 
      if (!start && !end) {
        clearTimeout(this.blurTimer);
        this.startControl.setValue(iso);
        this.endControl.setValue('');
        this.rawStart = this.formatDisplay(iso);
        this.rawEnd = '';
        this.valueChange.emit({ start: iso, end: '' });
        return;
      }
 
      if (start && !end && iso < start) {
        clearTimeout(this.blurTimer);
        this.startControl.setValue(iso);
        this.endControl.setValue('');
        this.rawStart = this.formatDisplay(iso);
        this.rawEnd = '';
        this.valueChange.emit({ start: iso, end: '' });
        return;
      }
 
      if (start && !end && iso >= start) {
        clearTimeout(this.blurTimer);
        this.endControl.setValue(iso);
        this.rawEnd = this.formatDisplay(iso);
        this.valueChange.emit({ start, end: iso });
        this.closeOverlay();
        return;
      }
 
      if (start && end) {
        if (iso < start) {
          clearTimeout(this.blurTimer);
          this.startControl.setValue(iso);
          this.endControl.setValue('');
          this.rawStart = this.formatDisplay(iso);
          this.rawEnd = '';
          this.valueChange.emit({ start: iso, end: '' });
        } else {
          clearTimeout(this.blurTimer);
          this.endControl.setValue(iso);
          this.rawEnd = this.formatDisplay(iso);
          this.valueChange.emit({ start, end: iso });
          this.closeOverlay();
        }
        return;
      }
 
      clearTimeout(this.blurTimer);
      this.startControl.setValue(iso);
      this.endControl.setValue('');
      this.rawStart = this.formatDisplay(iso);
      this.rawEnd = '';
      this.valueChange.emit({ start: iso, end: '' });
    }
  }
 
  protected isSelected(iso: string): boolean {
    if (this.mode === 'single') {
      if (this.singleFocused && this.pendingIso) return this.pendingIso === iso;
      return this.normalizeToIsoDate(this.control?.value) === iso;
    }
    const start = (this.startFocused && this.pendingStartIso)
      ? this.pendingStartIso
      : (this.normalizeToIsoDate(this.startControl?.value) || '');
    const end = (this.endFocused && this.pendingEndIso)
      ? this.pendingEndIso
      : (this.normalizeToIsoDate(this.endControl?.value) || '');
    return iso === start || iso === end;
  }
 
  protected isInRange(iso: string): boolean {
    if (this.mode === 'single') return false;
    const start = (this.startFocused && this.pendingStartIso)
      ? this.pendingStartIso
      : (this.normalizeToIsoDate(this.startControl?.value) || '');
    const end = (this.endFocused && this.pendingEndIso)
      ? this.pendingEndIso
      : (this.normalizeToIsoDate(this.endControl?.value) || '');
    if (!start || !end) return false;
    return iso > start && iso < end;
  }
 
  /**
   * Called straight from the template, so it runs on every change-detection pass.
   * A month's grid is fixed once its first day is known, so the 42 `Date` objects
   * are built once per month and reused; without this the identity of every day
   * object changed each pass and forced the whole grid to re-render.
   */
  protected getCalendarDays(monthDate: Date): CalendarDay[] {
    const start = this.startOfMonth(monthDate);
    const key = start.getTime();
    const cached = this.calendarDaysCache.get(key);
    if (cached) return cached;

    const firstWeekday = start.getDay();
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - firstWeekday);

    const days: CalendarDay[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + i);
      days.push({
        iso: this.toIsoDate(date),
        day: date.getDate(),
        inCurrentMonth:
          date.getMonth() === monthDate.getMonth() &&
          date.getFullYear() === monthDate.getFullYear(),
      });
    }

    if (this.calendarDaysCache.size >= DatePickerReusableComponent.calendarCacheLimit) {
      this.calendarDaysCache.clear();
    }
    this.calendarDaysCache.set(key, days);
    return days;
  }
 
  protected formatDisplay(value: unknown): string {
    const iso = this.valueToIso(value);
    if (!iso) return this.placeholder;
    const parsed = this.parseIsoDate(iso);
    if (!parsed) return this.placeholder;
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  protected get singleDisplayValue(): string {
    if (this.singleFocused) return this.rawSingle;
    return this.control?.value ? this.formatDisplay(this.control.value) : '';
  }

  protected get startDisplayValue(): string {
    if (this.startFocused) return this.rawStart;
    return this.startControl?.value ? this.formatDisplay(this.startControl.value) : '';
  }

  protected get endDisplayValue(): string {
    if (this.endFocused) return this.rawEnd;
    return this.endControl?.value ? this.formatDisplay(this.endControl.value) : '';
  }
 
  protected prevMonth(which: 'left' | 'right'): void {
    if (which === 'left') this.leftMonth = this.addMonths(this.leftMonth, -1);
    else this.rightMonth = this.addMonths(this.rightMonth, -1);
  }
 
  protected nextMonth(which: 'left' | 'right'): void {
    if (which === 'left') this.leftMonth = this.addMonths(this.leftMonth, 1);
    else this.rightMonth = this.addMonths(this.rightMonth, 1);
  }
 
  protected onMonthChange(which: 'left' | 'right', value: string): void {
    const month = Number(value);
    if (Number.isNaN(month) || month < 0 || month > 11) return;
    if (which === 'left') this.leftMonth = new Date(this.leftMonth.getFullYear(), month, 1);
    else this.rightMonth = new Date(this.rightMonth.getFullYear(), month, 1);
  }
 
  protected onYearChange(which: 'left' | 'right', value: string): void {
    const year = Number(value);
    if (Number.isNaN(year)) return;
    if (which === 'left') this.leftMonth = new Date(year, this.leftMonth.getMonth(), 1);
    else this.rightMonth = new Date(year, this.rightMonth.getMonth(), 1);
  }
 
  private updateOverlayPosition(): void {
    if (!this.triggerButton) return;
    const rect = this.triggerButton.nativeElement.getBoundingClientRect();
    const picker = DatePickerReusableComponent;
    const gap = picker.overlayGap;
    const margin = picker.viewportMargin;
    const room = window.innerWidth - 2 * margin;
    this.rangeSideBySide = this.mode === 'range' && room >= 2 * picker.monthWidth;
    const dropdownHeight =
      this.mode !== 'range'
        ? picker.overlayMinHeight
        : this.rangeSideBySide
          ? picker.rangeSideBySideHeight
          : picker.rangeStackedHeight;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const calendarWidth = Math.min(picker.monthWidth * (this.rangeSideBySide ? 2 : 1), room);
    // Beside the trigger when it fits; otherwise moved left so the whole calendar stays on screen.
    const left = Math.max(margin, Math.min(rect.left, window.innerWidth - calendarWidth - margin));
    this.openAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    if (this.openAbove) {
      this.overlayStyle = {
        position: 'fixed',
        left: `${Math.floor(left)}px`,
        width: `${Math.floor(calendarWidth)}px`,
        bottom: `${Math.floor(window.innerHeight - rect.top + gap)}px`,
        top: 'auto',
        zIndex: '99999',
        opacity: '1',
        pointerEvents: 'auto',
      };
    } else {
      this.overlayStyle = {
        position: 'fixed',
        left: `${Math.floor(left)}px`,
        width: `${Math.floor(calendarWidth)}px`,
        top: `${Math.floor(rect.bottom + gap)}px`,
        bottom: 'auto',
        zIndex: '99999',
        opacity: '1',
        pointerEvents: 'auto',
      };
    }
    this.cdr.detectChanges();
  }
 
  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }
 
  private addMonths(date: Date, delta: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + delta, 1);
  }
 
  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
 
  private getMaxSelectableDate(): Date {
    const candidates: Date[] = [];
    if (this.disableFutureDates) {
      candidates.push(new Date());
    }
    const parsedMaxDate = this.parseIsoDate(this.maxDate);
    if (parsedMaxDate) {
      candidates.push(parsedMaxDate);
    }
    if (candidates.length === 0) {
      return new Date(DatePickerReusableComponent.maxYearOption, 11, 31);
    }
    return candidates.reduce((earliest, current) =>
      current.getTime() < earliest.getTime() ? current : earliest,
    );
  }
 
  /**
   * `isDateDisabled` runs ~250 times per change-detection pass (42 days x 2 months,
   * queried three times each by the template). Recomputing the bound every call
   * allocated a `Date` and re-ran the ISO regex each time, so it is memoised and
   * refreshed whenever the calendar opens.
   */
  private getMaxSelectableIsoDate(): string {
    this.maxSelectableIso ??= this.toIsoDate(this.getMaxSelectableDate());
    return this.maxSelectableIso;
  }
 
  private closeOverlay(): void {
    this.isOpen = false;
    this.unbindGlobalListeners();
  }
 
  private bindGlobalListeners(): void {
    document.addEventListener('scroll', this.scrollListener, { capture: true, passive: true });
    window.addEventListener('resize', this.scrollListener, { passive: true });
    document.addEventListener('pointerdown', this.outsideClickListener, true);
  }
 
  private unbindGlobalListeners(): void {
    document.removeEventListener('scroll', this.scrollListener, true);
    window.removeEventListener('resize', this.scrollListener);
    document.removeEventListener('pointerdown', this.outsideClickListener, true);
  }
 
  private parseIsoDate(value: unknown): Date | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const isoMatch = raw.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:T.*)?$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return this.buildValidatedDate(Number(year), Number(month), Number(day));
    }
    const dmyMatch = raw.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (dmyMatch) {
      const [, day, month, year] = dmyMatch;
      return this.buildValidatedDate(Number(year), Number(month), Number(day));
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
 
  private buildValidatedDate(year: number, month: number, day: number): Date | null {
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return parsed;
  }
 
  private normalizeToIsoDate(value: unknown): string | null {
    const parsed = this.parseIsoDate(value);
    if (!parsed) return null;
    return this.toIsoDate(parsed);
  }
}
 
 